import React from 'react'
import { Stack, Box, Typography, ButtonBase } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'

/**
 * Sélecteur à deux options.
 *
 * Les deux boutons carrés de 100px côte à côte tronquaient leurs libellés dès
 * 375px de large (« Consommation de produits » sur trois lignes serrées).
 * Les options sont maintenant des cartes pleine largeur empilées, ce qui laisse
 * la place au texte, augmente la surface tactile et rend l'état sélectionné
 * lisible autrement que par la seule couleur (pictogramme + bordure).
 *
 * Le composant est entièrement piloté par `value` : l'ancienne copie locale de
 * l'état ignorait toute mise à jour venue du parent.
 */
const SquareRadioButtons = ({
  value,
  onChange,
  valueLeft,
  valueRight,
  valueLeftLabel,
  valueRightLabel,
  valueLeftIcon,
  valueRightIcon,
}) => {
  const options = [
    { value: valueLeft, label: valueLeftLabel, icon: valueLeftIcon },
    { value: valueRight, label: valueRightLabel, icon: valueRightIcon },
  ]

  return (
    <Stack
      spacing={1.5}
      direction={{ xs: 'column', sm: 'row' }}
      role="radiogroup"
      sx={{ width: '100%' }}
    >
      {options.map((option) => {
        const selected = value === option.value
        return (
          <ButtonBase
            key={option.value}
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            sx={{
              flex: 1,
              minHeight: 84,
              px: 2,
              py: 2,
              borderRadius: '16px',
              textAlign: 'left',
              justifyContent: 'flex-start',
              border: '2px solid',
              borderColor: selected ? 'primary.main' : 'divider',
              bgcolor: selected ? 'rgba(232,78,27,0.12)' : 'background.paper',
              transition: 'border-color 160ms ease, background-color 160ms ease',
              '&:active': { transform: 'scale(0.99)' },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
              {option.icon ? (
                <Box
                  sx={{
                    color: selected ? 'primary.main' : 'text.secondary',
                    display: 'flex',
                    '& .MuiSvgIcon-root': { fontSize: 30 },
                  }}
                >
                  {option.icon}
                </Box>
              ) : null}
              <Typography
                variant="subtitle1"
                sx={{ flexGrow: 1, minWidth: 0, color: selected ? '#FFFFFF' : 'text.secondary' }}
              >
                {option.label}
              </Typography>
              {selected ? (
                <CheckCircleIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ color: 'text.disabled', flexShrink: 0 }} />
              )}
            </Stack>
          </ButtonBase>
        )
      })}
    </Stack>
  )
}

export default SquareRadioButtons
